import { trackUserSessionEvents, trackUserSession } from '../api/api.pleased';
import { EventLog, SessionLog } from '../api/models.pleased';
import { randomUUID } from '../bot/lib';
import { EVENT_TYPES } from './events';
import { LogEvent, SessionUpdate, PageTrackEventParams, SessionTrackEventParams, LogEventParams } from './types';
import { store } from '@/store';
import { devLog } from '@/common/debug';
import { logger } from '@/common/api/logger';
import { parseErrorMessage } from '@/common/error';

class EventLogger {
  static instance: EventLogger;

  session: SessionLog | undefined;

  logQueue: EventLog[] = [];
  flushedLogs: EventLog[] = [];
  flushTimeout: ReturnType<typeof setTimeout> | null = null;
  debounceDelay = 1000;

  MAX_REQUEST_WAITING_MS = 3_000;

  flushingStarted = false;

  constructor() {
    if (!EventLogger.instance) {
      EventLogger.instance = this;

      this.logQueue = this.logQueue || [];
    }

    return EventLogger.instance;
  }

  get sessionId() {
    return this.session?.sessionId;
  }

  track = (event: LogEvent) => {
    if (!this.session) {
      devLog.error('No session found');
      return;
    }

    const { type = EVENT_TYPES.GENERAL, name, selection = null, metadata = {} } = event;

    const log = {
      sessionId: this.session.sessionId,
      eventId: randomUUID(),
      eventType: type,
      eventName: name,
      eventTime: new Date().toISOString(),

      ...(selection ? { eventSelection: selection } : {}),
      ...(Object.keys(metadata).length ? { metadata: JSON.stringify(metadata) } : {}),
    };

    this.logQueue.push(log);
    this.debounceFlushLogs();
  };

  trackSessionStart = (event: SessionTrackEventParams) => {
    const { sessionParams, ...rest } = event;

    if (this.session) {
      devLog.debug('Session already exists');
      return;
    }

    this.createUserSession(sessionParams);
    this.track({
      type: EVENT_TYPES.SESSION_START,
      name: EVENT_TYPES.SESSION_START,
      selection: this.sessionId,
      ...rest,
    });
  };

  trackSessionEndEvent = () => {
    this.track({
      type: EVENT_TYPES.SESSION_END,
      name: EVENT_TYPES.SESSION_END,
    });
  };

  trackSessionEnd = async () => {
    await this.updateUserSession({ sessionEndTime: new Date().toISOString() });
  };

  trackPageEvent = (event: PageTrackEventParams) => {
    const { enter, ...rest } = event;
    const type = enter ? EVENT_TYPES.PAGE_ENTER : EVENT_TYPES.PAGE_LEAVE;
    this.track({ type, ...rest });
  };

  trackClick = (event: LogEventParams) => {
    this.track({ type: EVENT_TYPES.BUTTON_CLICK, ...event });
  };

  trackAiEvent = (event: LogEventParams) => {
    this.track({ type: EVENT_TYPES.AI, ...event });
  };

  trackCriticalEvent = (event: LogEventParams) => {
    this.track({ type: EVENT_TYPES.CRITICAL, ...event });
  };

  trackVoiceEvent = (event: LogEventParams) => {
    this.track({ type: EVENT_TYPES.VOICE, ...event });
  };

  startFlushingLogs = () => {
    this.flushingStarted = true;

    // Track existing session only when flushing events
    this.updateUserSession({});
    this.setupTabCloseListener();
  };

  private debounceFlushLogs = () => {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }

    this.flushTimeout = setTimeout(() => this.flushLogs(), this.debounceDelay);
  };

  flushLogs = async () => {
    if (!this.logQueue.length || !this.flushingStarted) {
      return;
    }

    const userStore = (
      store.state as {
        user: { pleasedUser: { userId?: string } };
      }
    ).user;

    const customerId = userStore.pleasedUser.userId || '';

    try {
      this.flushedLogs = this.logQueue;
      this.logQueue = [];

      await trackUserSessionEvents({
        events: this.flushedLogs,
        customerId,
      });
    } catch (e) {
      logger.error('Error flushing logs:', { error: parseErrorMessage(e) });
      this.logQueue = [...this.flushedLogs, ...this.logQueue];
      this.flushedLogs = [];
    }
  };

  private createUserSession = async (sessionParams?: SessionUpdate) => {
    if (this.session) {
      devLog.debug('Session already exists');
      return;
    }

    this.session = {
      sessionId: randomUUID(),
      sessionStartTime: new Date().toISOString(),

      ...(sessionParams || {}),
    };
  };

  updateUserSession = async (session: SessionUpdate) => {
    if (!this.session) {
      devLog.error('No session found');
      return;
    }

    this.session = {
      ...this.session,
      ...session,
    };

    const userStore = (
      store.state as {
        user: { pleasedUser: { userId?: string } };
      }
    ).user;

    const userId = userStore.pleasedUser.userId || '';

    const trackSessionParams = {
      ...this.session,
      customerId: userId,
    };

    await trackUserSession(trackSessionParams);
  };

  // Should use `syncLogs` before calling this method
  restartUserSession = async (params: SessionTrackEventParams) => {
    if (!this.session) {
      devLog.error('No session found');
      return;
    }

    // Reset logs before new session
    this.session = undefined;
    this.flushedLogs = [];
    this.logQueue = [];

    this.trackSessionStart(params);
    await this.updateUserSession({});
  };

  private setupTabCloseListener = () => {
    document.addEventListener('visibilitychange', () => {
      const pageHidden = document.visibilityState === 'hidden';

      if (!this.flushingStarted || !pageHidden) {
        return;
      }

      this.flushLogs();
    });
  };
}

export const analytics = new EventLogger();
